import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ActivateAccountAction, ResendActivationAction } from "../../store/action";
import AuthShell from "../ui/AuthShell";
import {
  fieldClass,
  labelClass,
  primaryButtonClass,
  textLinkClass,
} from "../ui/formStyles";

/**
 * Landing page for the emailed activation link (/activate?token=...).
 *
 * Redeems the token on mount and reports the outcome. When the link is dead it
 * offers the way out — request a new one — rather than leaving the user on a
 * message with nothing to click.
 */
const Activate = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const [status, setStatus] = useState("working");
  const [message, setMessage] = useState("");

  // Activation spends the token, so it must happen exactly once. React 18 runs
  // effects twice in StrictMode, and without this guard the second pass would
  // redeem an already-consumed token and report failure over a success.
  const attempted = useRef(false);

  useEffect(() => {
    if (attempted.current) return;
    attempted.current = true;

    if (!token) {
      setStatus("error");
      setMessage(t("Activate.missingToken"));
      return;
    }

    const run = async () => {
      try {
        const data = await ActivateAccountAction(token);
        setStatus("success");
        setMessage(data?.message || t("Activate.activated"));
      } catch (error) {
        setStatus("error");
        setMessage(error.response?.data?.message || t("Activate.failed"));
      }
    };

    run();
    // `t` changes identity when the language does; the attempted guard above
    // makes the re-run a no-op, so the token is still only ever spent once.
  }, [token, t]);

  return (
    <AuthShell className="activate-content w-full max-w-md">
      <div className="text-center">
        <StatusIcon status={status} />

        <h1 className="activate-title mt-4 text-fluid-xl font-bold tracking-tight text-ink-900">
          {status === "working" && t("Activate.working")}
          {status === "success" && t("Activate.success")}
          {status === "error" && t("Activate.error")}
        </h1>

        {status !== "working" && (
          <p
            className="mt-2 text-fluid-sm text-ink-500"
            role={status === "error" ? "alert" : undefined}
          >
            {message}
          </p>
        )}
      </div>

      {status === "success" && (
        <Link to="/login" className={`${primaryButtonClass} mt-7`}>
          {t("Activate.continueToLogin")}
        </Link>
      )}

      {status === "error" && <ResendActivation />}

      <span className="mt-6 block text-center text-fluid-sm text-ink-500">
        {t("Activate.backTo")}{" "}
        <Link to="/login" className={textLinkClass}>
          {t("Activate.loginLink")}
        </Link>
      </span>
    </AuthShell>
  );
};

const StatusIcon = ({ status }) => {
  const presentation = {
    working: { icon: "bx bx-loader-alt animate-spin", tone: "bg-brand-50 text-brand-600" },
    success: { icon: "bx bx-check", tone: "bg-brand-50 text-brand-600" },
    error: { icon: "bx bx-x", tone: "bg-red-50 text-red-600" },
  }[status];

  return (
    <span
      className={`inline-flex size-12 items-center justify-center rounded-xl ${presentation.tone}`}
    >
      <i className={`${presentation.icon} text-2xl`} aria-hidden="true" />
    </span>
  );
};

/**
 * Request a replacement link.
 *
 * The API answers identically whether or not the address has a pending account,
 * so this shows one neutral confirmation and never reports "no such account" —
 * echoing a difference here would undo that on the client.
 */
const ResendActivation = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState("");

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSending(true);
    try {
      const data = await ResendActivationAction(email);
      setSent(data?.message || t("Activate.resend.sent"));
    } catch (error) {
      setSent(t("Activate.resend.sent"));
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <p className="mt-7 rounded-lg bg-brand-50 px-4 py-3 text-center text-fluid-sm text-ink-700">
        {sent}
      </p>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="mt-7">
      <div className="form-item">
        <label htmlFor="activate-email" className={labelClass}>
          {t("Activate.resend.label")}
        </label>
        <input
          type="email"
          id="activate-email"
          name="email"
          className={fieldClass}
          placeholder={t("Activate.resend.email")}
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          autoComplete="email"
          required
        />
      </div>

      <div className="form-item mt-4">
        <button type="submit" className={primaryButtonClass} disabled={sending}>
          {sending
            ? t("Activate.resend.submitting")
            : t("Activate.resend.submit")}
        </button>
      </div>
    </form>
  );
};

export default Activate;
